import {QueryCacheKeys} from './cache';
import {getCustomers} from './customers';
import {getInvoices} from './invoices';
import {createNote, deleteNote, getNotes, updateNote} from './notes';
import {
	createProduct,
	createProductVariant,
	getProducts,
	updateProduct,
	updateProductVariant,
} from './products';
import {createProject, getProject, getProjects, updateProject} from './projects';
import {createSale, getSales} from './sales';
import {getTags} from './tags';
import {getTransaction, getTransactions} from './transactions';
import {createTrip, deleteTrip, getTrips} from './trips';
import type {UseMutationOptions} from '@tanstack/react-query';
import {DateTime} from 'luxon';
import {orderBy} from 'shared/src/list';
import {groupBy} from 'shared/src/map';
import type {
	Customer,
	ID,
	Note,
	Product,
	ProductVariant,
	Project,
	Sale,
	Tag,
	TransactionFilters,
	Trip,
} from 'shared/src/models';

export const Queries = {
	customers: () => ({
		queryKey: QueryCacheKeys.customers(),
		queryFn: getCustomers,
		select: (customers: Customer[]) => orderBy(customers, 'name'),
	}),
	invoices: () => ({
		queryKey: QueryCacheKeys.invoices(),
		queryFn: () => getInvoices(),
	}),
	notes: (ids: string[]) => ({
		queryKey: QueryCacheKeys.notes(ids),
		queryFn: () => getNotes({ids}),
	}),
	products: () => ({
		queryKey: QueryCacheKeys.products(),
		queryFn: getProducts,
	}),
	project: (projectId: string) => ({
		queryKey: QueryCacheKeys.project(projectId),
		queryFn: () => getProject(projectId),
	}),
	projects: () => ({
		queryKey: QueryCacheKeys.projects(),
		queryFn: () => getProjects(),
	}),
	sales: (startDate: DateTime, endDate: DateTime) => ({
		queryKey: ['sales', 'startDate', startDate, 'endDate', endDate],
		queryFn: () => getSales({startDate, endDate}),
	}),
	tags: () => ({
		queryKey: QueryCacheKeys.tags(),
		queryFn: getTags,
		select: (tags: Tag[]): Tag[] => {
			const topLevelTags = orderBy(
				tags.filter((tag) => !tag.parentTagId),
				'name',
			);
			const childTagsByParentId = groupBy(tags, (tag) => tag.parentTagId);
			return topLevelTags.flatMap((tag) => [
				tag,
				...orderBy(childTagsByParentId.get(tag.id) ?? [], 'name'),
			]);
		},
	}),
	transaction: (id: string) => ({
		queryKey: QueryCacheKeys.transaction(id),
		queryFn: () => getTransaction(id),
	}),
	transactions: (filters: TransactionFilters) => ({
		queryKey: QueryCacheKeys.transactions(filters),
		queryFn: () => getTransactions(filters),
	}),
	trips: (year: number) => ({
		queryKey: QueryCacheKeys.trips(year),
		queryFn: () => getTrips({year}),
	}),
};

export const Mutations = {
	createNote: (): UseMutationOptions<
		Note,
		unknown,
		{
			note: Parameters<typeof createNote>[0];
			projectId: Parameters<typeof createNote>[1];
		}
	> => ({
		mutationFn: (variables) => createNote(variables.note, variables.projectId),
	}),
	createProduct: (): UseMutationOptions<
		Product,
		unknown,
		{product: Parameters<typeof createProduct>[0]}
	> => ({
		mutationFn: (variables) => createProduct(variables.product),
	}),
	createProductVariant: (): UseMutationOptions<
		ProductVariant,
		unknown,
		{
			productId: Parameters<typeof createProductVariant>[0];
			productVariant: Parameters<typeof createProductVariant>[1];
		}
	> => ({
		mutationFn: (variables) => createProductVariant(variables.productId, variables.productVariant),
	}),
	createProject: (): UseMutationOptions<
		Project,
		unknown,
		{project: Parameters<typeof createProject>[0]}
	> => ({
		mutationFn: (variables) => createProject(variables.project),
	}),
	createSale: (): UseMutationOptions<Sale, unknown, {sale: Parameters<typeof createSale>[0]}> => ({
		mutationFn: (variables) => createSale(variables.sale),
	}),
	createTrip: (): UseMutationOptions<Trip, unknown, {trip: Parameters<typeof createTrip>[0]}> => ({
		mutationFn: (variables) => createTrip(variables.trip),
	}),
	deleteNote: (): UseMutationOptions<void, unknown, {id: ID}> => ({
		mutationFn: (variables) => deleteNote(variables.id),
	}),
	deleteTrip: (): UseMutationOptions<void, unknown, {id: ID}> => ({
		mutationFn: (variables) => deleteTrip(variables.id),
	}),
	updateNote: (): UseMutationOptions<Note, unknown, {note: Note}> => ({
		mutationFn: (variables) => updateNote(variables.note),
	}),
	updateProduct: (): UseMutationOptions<
		Product,
		unknown,
		{product: Parameters<typeof updateProduct>[0]}
	> => ({
		mutationFn: (variables) => updateProduct(variables.product),
	}),
	updateProductVariant: (): UseMutationOptions<
		ProductVariant,
		unknown,
		{productVariant: Parameters<typeof updateProductVariant>[0]}
	> => ({
		mutationFn: (variables) => updateProductVariant(variables.productVariant),
	}),
	updateProject: (): UseMutationOptions<Project, unknown, {project: Project}> => ({
		mutationFn: (variables) => updateProject(variables.project),
	}),
};
